<script lang="ts">
	/**
	 * The self-serve refund control (Design §9.6).
	 *
	 * Three decisions are worth stating, because each is the section's actual requirement rather
	 * than a styling choice:
	 *
	 * - **The eligibility comes from the list, not from a request per row.** Every invoice and order
	 *   already carries it inline; asking the server per row would be an N+1 the moment a customer
	 *   has a year of invoices. `eligibility === null` means there is nothing to evaluate yet — an
	 *   order whose invoice has not been raised — so nothing renders at all.
	 * - **The confirm step re-checks, though.** That is what the single-invoice endpoint exists for:
	 *   a page left open while the refund window closed, or while the annual limit was reached in
	 *   another tab, otherwise shows an enabled button and answers a considered click with a raw
	 *   server rejection. One request at the moment of the decision costs nothing and replaces that
	 *   with the same human sentence the list would have shown.
	 * - **An ineligible purchase shows the reason and a way to reach a human**, never a disabled
	 *   button and never silence. §9.6 is explicit: refusing silently teaches customers to open a
	 *   ticket anyway, with more irritation. The copy is the server's, so what the list says and
	 *   what the confirm step says cannot drift apart.
	 * - **The note is optional.** `PromptButton` collects one and the API accepts one, but a
	 *   customer who has nothing to add must not be stopped by a required field — which is what the
	 *   permissive `inputValidator` below is for.
	 */

	import { createEventDispatcher } from 'svelte';
	import PromptButton from './PromptButton.svelte';
	import { getRefundEligibility, requestRefund } from '../AppNetwork';
	import { configurationStore } from '../AppConfiguration';
	import { formatAmount } from '../stores/storefront';
	import { toastError, toastSuccess } from '../stores/toast';
	import type { RefundEligibility } from '../AppTypes';

	/** The invoice the refund is issued against — an order refunds through its invoice. */
	export let invoiceUUID: string | null = null;

	/** The inline answer from `GET /api/account/invoices` or `/orders`. */
	export let eligibility: RefundEligibility | null = null;

	/**
	 * What is being refunded, named in the dialog's headline.
	 *
	 * A customer refunding one row out of a year of invoices is deciding about *that* row, and the
	 * amount alone does not identify it — two invoices for the same plan in the same currency are
	 * the same number. Null where the row does not know (an invoice with no product behind it), and
	 * the headline then names only the amount rather than inventing a name for it.
	 */
	export let productName: string | null = null;

	export let size: 'xs' | 'sm' | 'md' = 'xs';

	let working: boolean = false;

	const dispatch = createEventDispatcher<{ refunded: { uuid: string } }>();

	$: supportEmail = $configurationStore?.supportEmail ?? '';
	$: amount =
		eligibility === null
			? null
			: $formatAmount(eligibility.refundableAmount, eligibility.currency);

	/**
	 * The headline: what is being refunded and for how much, as one short line.
	 *
	 * It was run together with the consequences into a single paragraph, so the two things a
	 * customer checks before saying yes — which purchase, and how much comes back — were the same
	 * weight as the warning after them and were skipped along with it. Both halves degrade rather
	 * than disappear: an unknown amount says "this payment", an unknown product names the amount
	 * alone, and neither ever renders a blank or the word "null".
	 */
	$: headline =
		productName === null || productName.trim().length === 0
			? `Refund ${amount ?? 'this payment'}?`
			: `Refund ${productName} for ${amount ?? 'the amount paid'}?`;

	async function fnRefund(note: string): Promise<void> {
		if (invoiceUUID === null) return;

		working = true;
		try {
			// Re-checked at the moment of the decision, not at the moment the page rendered. The
			// server enforces this regardless; what this buys is the *reason*, in the server's own
			// words, instead of a rejection the customer has to interpret.
			const current = await getRefundEligibility(invoiceUUID);
			if (!current.eligible) {
				eligibility = current;
				toastError(current.message);
				return;
			}

			const refund = await requestRefund(invoiceUUID, note);
			toastSuccess(
				`Refunded ${$formatAmount(refund.amount, refund.currency) ?? ''}. It usually reaches your account within a few working days.`
			);
			dispatch('refunded', { uuid: refund.uuid });
		} catch (error) {
			// The server's wording, not ours: it names the clause that refused and the date it
			// applies to, which is the only version of this message worth reading.
			toastError((error as Error).message);
		} finally {
			working = false;
		}
	}
</script>

{#if eligibility !== null && invoiceUUID !== null}
	{#if eligibility.eligible}
		<PromptButton
			color="alternative"
			{size}
			disabled={working}
			icon="money-bill-transfer"
			action="a note"
			confirmText={headline}
			confirmDetail="Access ends straight away, and any subscription it belongs to is cancelled. Tell us why, if you like. It is optional."
			confirmButtonText="Refund"
			inputPlaceholder="Why are you refunding? (optional)"
			inputValidator={() => true}
			on:confirm={event => fnRefund(String(event.detail ?? ''))}
		>
			{working ? 'Refunding…' : 'Refund'}
		</PromptButton>
	{:else}
		<div class="text-xs text-default max-w-xs" data-testid="refund-unavailable">
			<span>{eligibility.message}</span>
			{#if supportEmail.length > 0}
				<a class="text-primary-500 hover:underline" href={`mailto:${supportEmail}`}>
					Contact support
				</a>
			{/if}
		</div>
	{/if}
{/if}
